import { SalamanderPianoSynth } from "@/lib/audio/synth";
import { Observable, type ReadonlyObservable } from "@/lib/studio/observable";
import type { ScheduleTicker } from "@/lib/app/schedule-ticker";
import type { Controler } from "@/lib/track/controler/controler";
import { Instrument } from "@/lib/track/instrument";
import type { Lane } from "@/lib/track/lane";
import { Lane as TrackLane } from "@/lib/track/lane";
import type { LocalTransport } from "@/lib/track/local-transport";
import { Scheduler } from "@/lib/track/scheduler";
import { Track } from "@/lib/track/track";
import type { Transport } from "@/lib/track/transport";
import { buildLaneView, type LaneView } from "../lane-view";
import { LaneControler } from "./lane-controler";

/**
 * Ce que le studio prête à une session pendant qu'elle est en scène.
 *
 * L'appareil d'un côté, la matière de l'autre : le transport, le ticker, la
 * sortie audio et le clavier du joueur sont propres au poste et ne bougent pas
 * d'une session à l'autre. Une session les emprunte, écrit dedans, puis les
 * rend.
 */
export interface SessionStage {
  readonly transport: Transport;
  readonly localTransport: LocalTransport;
  readonly ticker: ScheduleTicker;
  readonly audioContext: AudioContext;

  /** Publie une piste dans la couche de présentation, ou l'en retire. */
  showTrack(track: Track): void;
  hideTrack(track: Track): void;

  /**
   * Dirige le clavier du joueur vers une lane, ou nulle part.
   *
   * Le `PianoControler` reste au studio — c'est le clavier de la personne, il
   * survit aux bascules — mais il ne possède plus sa lane : sans ça, ce qu'on
   * a joué en séance suivrait dans la session solo suivante.
   */
  routePiano(lane: Lane | null): void;
}

/**
 * Une session de studio : la **matière** qu'on joue, et le temps sur lequel on
 * la joue.
 *
 * # Ce qui est ici, et ce qui reste au studio
 *
 * Ici : les pistes, leurs lanes, les ordonnanceurs de ces lanes, l'origine
 * temporelle, l'état de pause. Au studio : le piano, les entrées (clavier,
 * MIDI, micro), le contexte audio, le ticker, la vue. La ligne de partage est
 * celle de l'appareil et du contenu — il n'y a qu'un poste, il peut y avoir
 * plusieurs sessions en mémoire, et une seule en scène puisqu'il n'y a qu'un
 * piano-roll.
 *
 * # Le `Scheduler` suit la matière, mais pas la mémoire
 *
 * Il tient un curseur dans une lane précise et une sortie audio précise : sa
 * durée de vie est celle du couplage entre les deux, donc celle de
 * l'activation. Les lanes, elles, survivent — on retrouve ses notes et son
 * synthé chaud en revenant. D'où deux gestes distincts : `activate` monte les
 * ordonnanceurs, `dispose` détruit la matière.
 *
 * # Le temps se rejoue à l'activation
 *
 * Il n'y a qu'un `Transport`, persistant : ce qui change à la bascule, c'est
 * son **état**, pas l'objet — sinon tous les `Scheduler` et tous les
 * `LocalTransport` qui le tiennent devraient être reconstruits. Une session
 * quittée gare sa position ; une session reprise réécrit une origine sur le
 * transport.
 *
 * # Elle ne sait rien du live
 *
 * Il n'y a pas de sous-classe « séance » ici, et c'est délibéré : le studio
 * n'a pas à connaître le réseau. Ce que la couche live change tient en deux
 * réglages qui ne nomment aucun pair — une origine imposée du dehors plutôt
 * que la position garée, et le refus de s'arrêter quand le son se tait. Tout
 * le reste — connexion, participants, écart d'horloges, trames — vit dans
 * `lib/live/`, qui *configure* une session de studio ordinaire.
 *
 * # Nommage
 *
 * `StudioSession` et non `Session` : `lib/live/live-session.ts` tient déjà une
 * `LiveSession`, qui est la session **réseau** — une connexion, des pairs, des
 * trames. Celle-ci contrôle le studio, pas le réseau.
 */
export class StudioSession {

  private readonly tracks = new Set<Track>();
  private readonly laneViews = new Map<Lane, LaneView>();
  private readonly schedulers = new Map<Lane, Scheduler>();
  /**
   * Par piste ajoutée : les lanes qu'on lui connaît, et le désabonnement de
   * `track.onChange`. C'est ce qui permet de démonter une lane partie — un
   * participant qui quitte la séance — au lieu de la laisser, avec son
   * synthé et son ordonnanceur, accrochée au transport jusqu'à la fin.
   */
  private readonly trackBindings = new Map<Track, { lanes: Set<Lane>; unsubscribe: () => void }>();

  private stage: SessionStage | null = null;

  /** La lane où le clavier du joueur écrit. Créée à la première activation. */
  private pianoLane: Lane | null = null;
  private pianoTrack: Track | null = null;

  /** Position au moment où la session a quitté la scène. */
  private parkedPosition = 0;

  /**
   * Origine imposée du dehors, sur l'horloge de position.
   *
   * `null` — le cas ordinaire — veut dire « reprends où je t'ai garée ». Une
   * valeur veut dire « ta position se déduit de cette origine-là », ce dont se
   * sert la couche live pour poser l'origine mesurée avec les pairs : on ne
   * revient pas où l'on était parti, on revient là où la séance en est.
   */
  private fixedOrigin: number | null = null;

  /**
   * La perte de la sortie audio met-elle la session en pause ?
   *
   * Vrai par défaut : seul devant son clavier, le silence de la sortie est le
   * silence de la séance. La couche live le met à faux — les autres continuent
   * de jouer, et s'arrêter les décalerait de nous sans les en informer.
   */
  private pausesOnAudioLoss = true;

  /**
   * Vrai quand c'est la perte de la sortie audio qui a gelé la session, et
   * elle seule. C'est ce que `audioOutputResumed` a le droit de rouvrir.
   */
  private pausedByAudioLoss = false;

  private readonly pausedHolder = new Observable<boolean>(false);

  /** La vue locale était-elle déjà figée quand la pause est arrivée ? */
  private viewWasDetached = false;

  /**
   * La vue locale suivait-elle le direct quand la pause est arrivée ?
   *
   * Une pause de séance fige la vue en la décrochant ; la reprise la
   * réancre sur une origine imposée (`resume(withOrigin)`), c'est-à-dire sur
   * la position que le service publie. Dégeler la vue « où elle en était »
   * — ce que fait `LocalTransport.play()` — la laissait alors en retard de
   * ce qui sépare la position figée de la position reprise : quelques
   * millisecondes, invisibles sur le rouleau, mais assez pour que
   * `isFollowingShared` réponde faux, que le bouton « Revenir au live »
   * s'allume dès l'entrée en séance, et qu'une note de partenaire cesse de
   * sonner tout de suite. Une vue qui suivait le direct avant la pause le
   * suit encore après ; une vue qui s'en était écartée — un défilement en
   * solo — reprend où elle en était, comme avant.
   */
  private viewWasFollowing = false;

  constructor(readonly label: string) {}

  // ── Le temps ─────────────────────────────────────────────────

  /**
   * Impose l'origine, ou rend la main à la position garée avec `null`.
   *
   * Prend effet à la prochaine activation, et tout de suite si la session est
   * déjà en scène — les relevés d'écart d'horloges arrivent quelques secondes
   * après la connexion, donc l'origine se recale en cours de route.
   */
  setFixedOrigin(origin: number | null): void {
    this.fixedOrigin = origin;

    if (this.stage !== null) {
      this.stage.transport.setOrigin(this.originAt(this.stage.transport.clock.currentTime));
    }
  }

  /** Voir `pausesOnAudioLoss`. */
  setPausesOnAudioLoss(pauses: boolean): void {
    this.pausesOnAudioLoss = pauses;
  }

  /** Ce que la session fait quand la sortie audio se tait. */
  audioOutputStopped(): void {
    if (!this.pausesOnAudioLoss || this.isPaused) {
      return;
    }

    this.pausedByAudioLoss = true;
    this.pause();
  }

  /**
   * La sortie audio est revenue : le temps redevient continu.
   *
   * # Le gel qui ne se rouvrait pas
   *
   * Sur iPhone, passer à une autre application suspend le contexte audio à
   * tous les coups. `audioOutputStopped` gèle alors la session — `frozenAt`
   * est posé sur le transport — et **rien, nulle part, ne le retirait** : le
   * bouton de lecture décroche et raccroche la *vue* (`LocalTransport`), il ne
   * parle pas de la session. La personne revenait donc dans un studio où
   * `Transport.elapsedTime()` rendait à jamais la même valeur.
   *
   * Le symptôme était trompeur, parce qu'il n'éteint presque rien : le
   * décompte se voit et s'entend — il se calcule sur `AudioContext.currentTime`
   * et n'interroge jamais la position —, le clavier sonne et s'allume, le
   * canvas dessine chaque image. Seules deux choses meurent, et ce sont
   * exactement les deux qui lisent la position : le roll ne défile plus, et
   * `LocalTransport.onSchedule` rend la main sans rien distribuer, donc plus
   * une note ne sonne. Un rechargement rendait tout, et lui seul.
   *
   * # Pourquoi ce n'est pas « la reprise est un geste »
   *
   * Cette règle-là tient toujours, et elle est respectée : la **lecture** ne
   * repart pas d'elle-même. La vue reste décrochée là où la pause l'a posée,
   * et c'est bien un geste qui la relance. Ce qu'on rouvre ici est le gel du
   * *temps*, qui n'a jamais été une décision de la personne : il vient de la
   * disparition du son, et il n'a plus lieu d'être dès que le son est là.
   *
   * Ne rouvre que ce que la perte du son a fermé. Une pause décidée ailleurs —
   * un partenaire, la couche live — ne connaît pas ce chemin : elle a posé
   * `pausesOnAudioLoss` à faux, et le drapeau n'est donc jamais levé.
   */
  audioOutputResumed(): void {
    if (!this.pausedByAudioLoss) {
      return;
    }

    this.pausedByAudioLoss = false;

    if (!this.isPaused) {
      return;
    }

    this.pausedHolder.set(false);
    this.stage?.transport.resume();
  }

  private originAt(now: number): number {
    return this.fixedOrigin ?? now - this.parkedPosition;
  }

  get isPaused(): boolean {
    return this.pausedHolder.value;
  }

  /**
   * Change à chaque bascule, y compris celles décidées ailleurs — la sortie
   * audio qui se tait, un partenaire qui met la séance en pause. C'est par là
   * qu'une couche extérieure apprend ce qu'elle n'a pas déclenché.
   */
  get pausedState(): ReadonlyObservable<boolean> {
    return this.pausedHolder;
  }

  /**
   * Gèle la session.
   *
   * Asymétrique par construction avec la reprise : rien ici ne repart tout
   * seul. Un contexte audio se suspend pour des raisons qui ne sont pas « la
   * personne veut une pause » — politique d'autoplay, changement de
   * périphérique, appel entrant — et la reprise reste un geste.
   */
  pause(atPosition?: number): void {
    if (this.isPaused) {
      return;
    }

    this.pausedHolder.set(true);

    // Geler à la position reçue, et non à la nôtre. Une pause qui vient d'un
    // partenaire arrive avec le retard du réseau ; s'arrêter « ici » plutôt
    // qu'« à cet endroit-là » décalerait la séance de la durée du trajet, à
    // chaque pause.
    //
    // Posée avant la garde de scène : une session hors scène — le temps d'une
    // bascule — doit garer la position ordonnée, sinon elle reviendrait au
    // mauvais endroit en remontant.
    if (atPosition !== undefined) {
      this.parkedPosition = atPosition;
      this.stage?.transport.setPosition(atPosition);
    }

    if (this.stage === null) {
      return;
    }

    this.viewWasDetached = this.stage.localTransport.isDetached;
    this.viewWasFollowing = this.stage.localTransport.isFollowingShared;
    this.stage.transport.pause();
    this.stage.localTransport.detach();
  }

  resume(withOrigin?: number): void {
    if (!this.isPaused) {
      return;
    }

    // Une reprise pleine solde la dette du son : il ne reste rien que le
    // retour de la sortie audio ait à rouvrir derrière.
    this.pausedByAudioLoss = false;
    this.pausedHolder.set(false);

    // L'ordre de ces deux gestes est porteur, et l'inverse était faux.
    //
    // `Transport.resume()` dégèle en **recalculant** l'origine depuis la
    // position gelée : c'est ce qu'il faut quand on reprend seul. Mais en
    // séance, l'origine vient du dehors — et la poser avant le dégel la faisait
    // écraser aussitôt. Chaque machine repartait alors de *sa* position figée,
    // à *son* instant : deux participants entrés à trente secondes d'écart
    // divergeaient de trente secondes, l'un jouant dans le passé de l'autre.
    //
    // On dégèle donc d'abord, puis on impose l'origine partagée.
    this.stage?.transport.resume();

    // Reprendre sur l'origine de l'émetteur plutôt que sur la nôtre. Les
    // horloges étant alignées, la reproduire donne exactement sa position —
    // quel que soit l'instant où l'ordre nous parvient. Retenue même hors
    // scène, pour la même raison que la position ci-dessus.
    if (withOrigin !== undefined) {
      this.setFixedOrigin(withOrigin);
    }

    if (this.stage === null) {
      return;
    }

    // Une vue déjà figée avant la pause le reste : la reprise rend la session
    // au mouvement, pas la personne à sa place.
    if (this.viewWasFollowing) {
      this.stage.localTransport.followShared();

      return;
    }

    if (!this.viewWasDetached) {
      this.stage.localTransport.play();
    }
  }

  /** Position courante de la session, en secondes. */
  position(): number {
    if (this.stage === null) {
      return this.parkedPosition;
    }

    return this.stage.transport.elapsedTime();
  }

  // ── Entrer et sortir de scène ────────────────────────────────

  get isActive(): boolean {
    return this.stage !== null;
  }

  activate(stage: SessionStage): void {
    if (this.stage !== null) {
      return;
    }

    this.stage = stage;

    const now = stage.transport.clock.currentTime;
    stage.transport.setOrigin(this.originAt(now));

    // Symétrique, et ça n'allait pas de soi : la branche en pause figeait la
    // vue locale, l'autre ne la dégelait pas. Or `LocalTransport` naît figé.
    // Une session activée en marche laissait donc une vue immobile — et une
    // note de partenaire, datée sur le transport partagé, n'y sonnait jamais :
    // le `Scheduler` l'écarte tant que la vue ne suit pas.
    //
    // `play()` et non `followShared()` : on dégèle sans effacer un déplacement
    // à la molette. Retomber au direct est un geste de l'utilisateur, pas une
    // conséquence de l'activation.
    if (this.isPaused) {
      stage.transport.pause();
      stage.localTransport.detach();
    } else {
      stage.transport.resume();
      stage.localTransport.play();
    }

    this.ensurePianoTrack(stage);

    for (const track of this.tracks) {
      stage.showTrack(track);

      for (const lane of track.getLanes()) {
        this.scheduleLane(lane);
      }
    }

    stage.routePiano(this.pianoLane);
  }

  deactivate(): void {
    const stage = this.stage;

    if (stage === null) {
      return;
    }

    this.parkedPosition = stage.transport.elapsedTime();

    stage.routePiano(null);

    for (const scheduler of this.schedulers.values()) {
      scheduler.destroy();
    }

    this.schedulers.clear();

    for (const track of this.tracks) {
      stage.hideTrack(track);
    }

    this.stage = null;
  }

  // ── La matière ───────────────────────────────────────────────

  /**
   * Ajoute une piste à cette session.
   *
   * Réservé à la session en scène : construire une piste demande le ticker, et
   * une piste montée à l'aveugle sur une session au repos serait une piste dont
   * personne n'ordonnance les lanes.
   */
  addTrack(controler: Controler): Track {
    const stage = this.requireStage();
    const track = new Track(controler, stage.ticker);

    this.tracks.add(track);

    const known = new Set<Lane>();

    // Réconcilie ce qu'on tient avec ce que la piste a : les lanes parties
    // sont démontées, les nouvelles montées. `Track` ne détruit pas une lane
    // qu'il retire — `LiveControler.forget` non plus — et sans ce ménage
    // l'ordonnanceur restait rappelé toutes les 25 ms à vie.
    const reconcile = () => {
      const current = track.getLanes();
      const currentSet = new Set(current);

      for (const lane of Array.from(known)) {
        if (currentSet.has(lane)) {
          continue;
        }

        known.delete(lane);
        this.forgetLane(lane);
      }

      for (const lane of current) {
        known.add(lane);
        this.laneViews.set(lane, buildLaneView(lane, controler));
        this.scheduleLane(lane);
      }
    };

    reconcile();
    this.trackBindings.set(track, { lanes: known, unsubscribe: track.onChange(reconcile) });

    stage.showTrack(track);

    return track;
  }

  removeTrack(track: Track): void {
    this.dismount(track);

    if (track === this.pianoTrack) {
      this.pianoTrack = null;
      this.pianoLane = null;
      this.stage?.routePiano(null);
    }

    track.destroy();
  }

  /**
   * Sort une piste de la session sans détruire ce qu'elle présentait.
   *
   * Le pendant de `removeTrack` pour un morceau qu'on met de côté plutôt que
   * de le refermer : les ordonnanceurs quittent le transport, la piste quitte
   * la présentation, mais ses lanes — notes, synthé chaud — restent à qui les
   * a confiées. C'est ce dont une séquence de morceaux a besoin pour que le
   * morceau que la tête vient de quitter puisse revenir tel quel. Voir
   * `Track.release`. Réservé aux pistes d'un morceau : la piste du piano
   * appartient à la session et ne se met pas de côté.
   */
  releaseTrack(track: Track): void {
    this.dismount(track);
    track.release();
  }

  /** Ce que retirer et mettre de côté ont en commun : tout, sauf le sort des lanes. */
  private dismount(track: Track): void {
    // Désabonné avant que la piste ne se défasse : sa réconciliation ne doit
    // pas repasser sur des lanes que le démontage retire déjà.
    this.trackBindings.get(track)?.unsubscribe();
    this.trackBindings.delete(track);

    // Les lanes sont relevées avant : la piste vide sa collection en partant.
    const lanes = track.getLanes();

    for (const lane of lanes) {
      this.schedulers.get(lane)?.destroy();
      this.schedulers.delete(lane);
      this.laneViews.delete(lane);
    }

    this.tracks.delete(track);
    this.stage?.hideTrack(track);
  }

  getTracks(): Track[] {
    return Array.from(this.tracks);
  }

  getLaneView(lane: Lane): LaneView | undefined {
    return this.laneViews.get(lane);
  }

  /** La lane du piano de cette session, `null` tant qu'elle n'a pas été en scène. */
  getPianoLane(): Lane | null {
    return this.pianoLane;
  }

  getPianoTrack(): Track | null {
    return this.pianoTrack;
  }

  /** Coupe tout ce qui sonne, sans rien démonter. */
  stopAllNotes(): void {
    for (const scheduler of this.schedulers.values()) {
      scheduler.stopAllNotes();
    }
  }

  /**
   * Détruit la session et sa matière — lanes, synthés, notes comprises.
   *
   * À distinguer de `deactivate`, qui ne fait que la sortir de scène. Une
   * session mémorisée est une session désactivée, pas détruite.
   */
  /** L'origine sur laquelle la session court, ou `null` si elle reprend où on l'a garée. */
  getFixedOrigin(): number | null {
    return this.fixedOrigin;
  }

  dispose(): void {
    this.deactivate();

    for (const binding of this.trackBindings.values()) {
      binding.unsubscribe();
    }

    this.trackBindings.clear();

    for (const track of this.tracks) {
      track.destroy();
    }

    this.tracks.clear();
    this.laneViews.clear();
    this.pianoTrack = null;
    this.pianoLane = null;
  }

  // ── Interne ──────────────────────────────────────────────────

  protected requireStage(): SessionStage {
    if (this.stage === null) {
      throw new Error("Cette session n'est pas en scène");
    }

    return this.stage;
  }

  private scheduleLane(lane: Lane): void {
    const stage = this.stage;

    if (stage === null || this.schedulers.has(lane)) {
      return;
    }

    this.schedulers.set(lane, new Scheduler(stage.localTransport, lane));
  }

  /**
   * Démonte une lane que sa piste a retirée : l'ordonnanceur quitte le
   * transport, la lane libère son synthé.
   */
  private forgetLane(lane: Lane): void {
    this.schedulers.get(lane)?.destroy();
    this.schedulers.delete(lane);
    this.laneViews.delete(lane);
    lane.destroy();
  }

  /**
   * La piste du piano, créée à la première entrée en scène.
   *
   * Un synthé à elle, jamais partagé : `SalamanderPianoSynth` indexe ses notes
   * par numéro MIDI seul et relâche « l'instance précédente » de chaque note,
   * si bien que deux lanes sur un même synthé se coupent mutuellement dès
   * qu'elles jouent le même do. Le coût qui justifiait le partage — trente
   * MP3 redécodés — a disparu avec le cache d'échantillons.
   */
  private ensurePianoTrack(stage: SessionStage): void {
    if (this.pianoTrack !== null) {
      return;
    }

    this.pianoLane = new TrackLane(
      0,
      new SalamanderPianoSynth(stage.audioContext),
      new Instrument("Piano", "Piano"),
    );

    // Muette : ce que la personne joue sort de son propre piano, le rejouer
    // depuis la lane le doublerait.
    this.pianoLane.mute();

    const track = new Track(
      new LaneControler(this.pianoLane, { kind: "live-piano" }, "Piano"),
      stage.ticker,
    );

    this.tracks.add(track);
    this.laneViews.set(this.pianoLane, {
      lane: this.pianoLane,
      origin: { kind: "live-piano" },
      colorToken: "live",
    });
    this.pianoTrack = track;
  }
}
